import React, { useEffect, useState, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Plus, Wifi, WifiOff, Edit2, Trash2, Star, Loader2, CheckCircle2, User, AtSign, Gift, MessageSquare, FileText, QrCode, Smartphone } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Slider } from '../../components/ui/slider';
import PageHeader from '../../components/PageHeader';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import api, { apiError, fmtDate } from '../../lib/api';

export default function Accounts() {
  const { t } = useTranslation();
  const [accs, setAccs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const dragId = useRef(null);
  const reorderAccs = (targetId) => {
    const from = dragId.current; dragId.current = null;
    if (!from || from === targetId) return;
    setAccs(prev => {
      const arr = [...prev];
      const fi = arr.findIndex(a => a.id === from), ti = arr.findIndex(a => a.id === targetId);
      if (fi < 0 || ti < 0) return prev;
      const [m] = arr.splice(fi, 1); arr.splice(ti, 0, m);
      api.post('/accounts/reorder', { ordered_ids: arr.map(a => a.id) }).catch(() => {});
      return arr;
    });
  };

  const load = async () => {
    try {
      const { data } = await api.get('/accounts');
      setAccs(data);
    } catch (e) {
      toast.error(apiError(e, 'Impossible de charger les comptes'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Live progress while a full-history import is running on any account
  useEffect(() => {
    if (!accs.some(a => a.sync_status === 'running')) return;
    const t = setInterval(() => {
      api.get('/accounts').then(({ data }) => setAccs(data)).catch(() => {});
    }, 4000);
    return () => clearInterval(t);
  }, [accs]);

  const remove = async (id) => {
    try {
      await api.delete(`/accounts/${id}`);
      setAccs(accs.filter(x => x.id !== id));
      toast.success('Compte supprimé');
    } catch (e) {
      toast.error(apiError(e, 'Échec de la suppression'));
    }
  };

  const [importingNotes, setImportingNotes] = useState(false);
  const importNotes = async () => {
    if (importingNotes) return;
    if (!window.confirm("Importer les notes privées Telegram de tous tes fans dans leur fiche ChatHub ?\n\n(Les notes déjà saisies ne sont pas écrasées — la note Telegram est ajoutée dessous. L'import tourne en arrière-plan ; sur un gros compte ça prend plusieurs minutes, tu peux continuer à utiliser ChatHub pendant ce temps.)")) return;
    setImportingNotes(true);
    const tid = toast.loading('Import des notes Telegram… démarrage');
    // The import now runs in the BACKGROUND (a 600-fan account takes 10-15 min,
    // far beyond any request timeout). We start it, then poll its progress.
    const finish = (st) => {
      const imp = st.imported || 0, nn = st.no_note || 0;
      toast.success(
        `${imp} note(s) importée(s) · ${nn} fan(s) sans note${st.flood_stopped ? ' · ralenti par la limite Telegram, relance plus tard pour finir le reste' : ''}`,
        { id: tid, duration: 9000 }
      );
      setImportingNotes(false);
    };
    const poll = async () => {
      try {
        const { data: st } = await api.get('/telegram/import-notes/status');
        if (st && st.running) {
          const pct = st.total ? Math.round((100 * (st.processed || 0)) / st.total) : 0;
          toast.loading(`Import des notes Telegram… ${st.processed || 0}/${st.total || '?'} (${pct}%) · ${st.imported || 0} importée(s)`, { id: tid });
          setTimeout(poll, 3000);
        } else {
          finish(st || {});
        }
      } catch (e) {
        toast.error(apiError(e, "Suivi interrompu — l'import continue côté serveur, recharge la page plus tard"), { id: tid });
        setImportingNotes(false);
      }
    };
    try {
      await api.post('/telegram/import-notes');
      setTimeout(poll, 2000);
    } catch (e) {
      toast.error(apiError(e, "Échec du lancement de l'import des notes"), { id: tid });
      setImportingNotes(false);
    }
  };
  const stopImportNotes = async () => {
    try {
      await api.post('/telegram/import-notes/cancel');
      toast.message("Arrêt de l'import en cours…");
    } catch (e) { toast.error(apiError(e, "Échec de l'arrêt")); }
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto fade-in" data-testid="accounts-page">
      <PageHeader
        title="Comptes"
        subtitle={`${t('dash.accountsSubtitle')} · ${accs.filter(a => a.status === 'ready').length} actifs`}
        badge={<Badge variant="outline" className="border-indigo-500/30 bg-indigo-500/10 text-indigo-300">{accs.length} au total</Badge>}
        actions={
          <div className="flex items-center gap-2">
            <Button data-testid="import-tg-notes" variant="outline" disabled={importingNotes} onClick={importNotes}
              className="border-white/10 bg-white/5 text-slate-200 hover:bg-white/10 h-9">
              {importingNotes ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <FileText size={14} className="mr-1.5" />}
              {importingNotes ? 'Import…' : 'Importer les notes Telegram'}
            </Button>
            {importingNotes && (
              <Button data-testid="import-tg-notes-stop" variant="outline" onClick={stopImportNotes}
                className="border-rose-500/30 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 h-9">
                Arrêter
              </Button>
            )}
            <NewAccount onCreated={load} />
          </div>
        }
      />

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-400" size={28} /></div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {accs.map(a => (
            <div key={a.id} draggable onDragStart={() => { dragId.current = a.id; }} onDragOver={e => e.preventDefault()} onDrop={() => reorderAccs(a.id)} className="cursor-grab active:cursor-grabbing">
              <AccountCard a={a} onEdit={() => setEditing(a)} onDelete={() => remove(a.id)} onChanged={load} />
            </div>
          ))}
        </div>
      )}

      <EditAccountDialog account={editing} onClose={() => setEditing(null)}
        onSaved={(updated) => { setAccs(accs.map(x => x.id === updated.id ? updated : x)); setEditing(null); }} />
    </div>
  );
}

function AccountCard({ a, onEdit, onDelete, onChanged }) {
  const { t } = useTranslation();
  const isReady = a.status === 'ready';
  const needsLogin = a.status === 'needs_login';
  const [reconnect, setReconnect] = useState(null); // { step, code, password, proxyId, me }

  const disconnect = async () => {
    if (!window.confirm(t('dash.disconnectConfirm', { name: a.name }))) return;
    try {
      await api.post('/telegram/disconnect', { account_id: a.id });
      toast.success(`${a.name} déconnecté`);
      onChanged?.();
    } catch (e) { toast.error(apiError(e, t('dash.disconnectFailed'))); }
  };

  return (
    <>
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden hover:border-indigo-500/30 transition-colors" data-testid={`account-card-${a.id}`}>
        <div className={`px-4 py-2 flex items-center justify-between text-xs ${isReady ? 'bg-emerald-500/10 border-b border-emerald-500/20' : needsLogin ? 'bg-amber-500/10 border-b border-amber-500/20' : 'bg-blue-500/10 border-b border-blue-500/20'}`}>
          <span className={`flex items-center gap-1.5 ${isReady ? 'text-emerald-300' : needsLogin ? 'text-amber-300' : 'text-blue-300'}`}>
            {isReady ? <><CheckCircle2 size={12} /> Connecté à Telegram</>
              : needsLogin ? <><WifiOff size={12} /> Non connecté</>
              : <><Loader2 size={12} className="animate-spin" /> Préparation… ({a.preparing_pct}%)</>}
          </span>
          <span className={`flex items-center gap-1 ${a.dropfans_enabled ? 'text-red-400' : 'text-slate-500'}`}>
            <Star size={11} className={a.dropfans_enabled ? 'fill-red-400' : ''} /> DropFans : {a.dropfans_enabled ? 'Activé' : 'Désactivé'}
          </span>
        </div>
        {a.sync_status === 'running' && (
          <div className="px-4 py-2 bg-sky-500/10 border-b border-sky-500/20" data-testid={`sync-progress-${a.id}`}>
            <div className="flex items-center justify-between text-[11px] text-sky-300 mb-1">
              <span className="flex items-center gap-1.5"><Loader2 size={11} className="animate-spin" /> Import de l&apos;historique des conversations…</span>
              <span className="font-semibold tabular-nums" data-testid={`sync-progress-count-${a.id}`}>{a.sync_done}/{a.sync_total || '…'} conversations</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-sky-400 transition-all duration-700" style={{ width: `${a.sync_total ? Math.max(3, Math.round((a.sync_done / a.sync_total) * 100)) : 3}%` }} />
            </div>
          </div>
        )}
        <div className="p-5">
          <div className="flex items-center gap-3 mb-4">
            {a.profile_photo_url ? (
              <img src={a.profile_photo_url.startsWith('http') ? a.profile_photo_url : `${process.env.REACT_APP_BACKEND_URL}${a.profile_photo_url}`} alt={a.name} className="w-12 h-12 rounded-xl object-cover shrink-0 border border-white/10" />
            ) : (
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${a.color} flex items-center justify-center text-white font-bold text-lg shrink-0`}>
                {a.initial}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="text-base font-semibold text-white">{a.name}</div>
                {isReady && <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px] px-1.5">Actif</Badge>}
                {a.telegram_username && <span className="text-[10px] text-sky-300">@{a.telegram_username}</span>}
              </div>
              <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                {isReady ? <Wifi size={11} className="text-emerald-400" /> : <WifiOff size={11} className="text-slate-500" />}
                {a.phone}
              </div>
            </div>
            <div className="flex gap-0.5">
              <Button data-testid={`edit-account-${a.id}`} variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:bg-white/5" onClick={onEdit}><Edit2 size={13} /></Button>
              <Button data-testid={`delete-account-${a.id}`} variant="ghost" size="icon" className="h-7 w-7 text-rose-400 hover:bg-rose-500/10" onClick={onDelete}><Trash2 size={13} /></Button>
            </div>
          </div>
          <div className="flex items-center gap-1.5 pt-3 border-t border-white/5">
            {isReady ? (
              <Button data-testid={`disconnect-account-${a.id}`} size="sm" variant="outline" className="flex-1 border-white/10 bg-white/5 text-slate-200 hover:bg-rose-500/10 hover:text-rose-300 h-7 text-[11px]" onClick={disconnect}><WifiOff size={11} className="mr-1" /> {t('dash.disconnect')}</Button>
            ) : (
              <Button data-testid={`connect-account-${a.id}`} size="sm" className="flex-1 bg-emerald-500 hover:bg-emerald-600 h-7 text-[11px]" onClick={() => setReconnect({ step: 'phone', code: '', password: '', proxyId: null, me: null })}><Wifi size={11} className="mr-1" /> Se connecter à Telegram</Button>
            )}
            <span className="text-[10px] text-slate-500 ml-1 whitespace-nowrap">{fmtDate(a.added_at)}</span>
          </div>
        </div>
      </div>

      {reconnect && (
        <ReconnectDialog account={a} state={reconnect} setState={setReconnect}
          onClose={() => setReconnect(null)} onDone={() => { setReconnect(null); onChanged?.(); }} />
      )}
    </>
  );
}

function ReconnectDialog({ account, state, setState, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const [phone, setPhone] = useState(account.phone || '');

  const sendCode = async () => {
    if (!phone.trim()) { toast.error('Numéro de téléphone requis'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/telegram/connect/start', { account_id: account.id, phone });
      setState({ ...state, step: 'code', proxyId: data.proxy_session_id });
      toast.success(`Code envoyé sur Telegram · session proxy #${data.proxy_session_id}`);
    } catch (e) { toast.error(apiError(e, 'Échec de l\'envoi du code')); } finally { setSaving(false); }
  };

  const verifyCode = async () => {
    if (!state.code.trim()) { toast.error('Code requis'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/telegram/connect/verify', { account_id: account.id, code: state.code });
      if (data.needs_password) { setState({ ...state, step: 'twofa' }); toast.info('2FA requise'); }
      else { setState({ ...state, step: 'success', me: data.me }); }
    } catch (e) { toast.error(apiError(e, 'Code invalide')); } finally { setSaving(false); }
  };

  const verify2FA = async () => {
    if (!state.password) { toast.error('Mot de passe requis'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/telegram/connect/2fa', { account_id: account.id, password: state.password });
      setState({ ...state, step: 'success', me: data.me });
    } catch (e) { toast.error(apiError(e, 'Échec de la 2FA')); } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2"><Wifi size={16} className="text-emerald-400" /> Connecter {account.name} à Telegram</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">Via proxy mobile Decodo Paris · spoof iPhone 15 Pro</DialogDescription>
        </DialogHeader>

        {state.step === 'phone' && (
          <div className="pt-2 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs">Numéro de téléphone (international, avec +)</Label>
              <Input data-testid="reconnect-phone-input" value={phone} onChange={e => setPhone(e.target.value)} className="bg-white/5 border-white/10 text-white h-10" placeholder="+33612345678" autoFocus />
            </div>
            <p className="text-[10px] text-slate-500">Un code de connexion sera envoyé via ton app Telegram ou par SMS.</p>
            <div className="flex justify-end gap-2"><Button variant="outline" className="border-white/10 bg-white/5 text-slate-300 hover:bg-white/10" onClick={onClose}>Annuler</Button>
              <Button data-testid="reconnect-send-code" className="bg-emerald-500 hover:bg-emerald-600" onClick={sendCode} disabled={saving}>{saving ? 'Envoi…' : 'Envoyer le code'}</Button>
            </div>
          </div>
        )}

        {state.step === 'code' && (
          <div className="pt-2 space-y-4">
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-300">✓ Code envoyé au {phone} via la session Decodo #{state.proxyId}</div>
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs">Code Telegram</Label>
              <Input data-testid="reconnect-code-input" value={state.code} onChange={e => setState({ ...state, code: e.target.value.replace(/\D/g, '').slice(0, 5) })} className="bg-white/5 border-white/10 text-white h-12 text-center text-2xl tracking-widest font-mono" placeholder="12345" autoFocus onKeyDown={e => e.key === 'Enter' && verifyCode()} />
            </div>
            <div className="flex justify-end gap-2"><Button variant="outline" className="border-white/10 bg-white/5 text-slate-300 hover:bg-white/10" onClick={() => { api.post('/telegram/connect/cancel', { account_id: account.id }).catch(() => {}); onClose(); }}>Annuler</Button>
              <Button data-testid="reconnect-verify" className="bg-emerald-500 hover:bg-emerald-600" onClick={verifyCode} disabled={saving || state.code.length < 4}>{saving ? 'Vérification…' : 'Vérifier'}</Button>
            </div>
          </div>
        )}

        {state.step === 'twofa' && (
          <div className="pt-2 space-y-4">
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-300">🔐 2FA activée — saisis le mot de passe cloud</div>
            <Input data-testid="reconnect-2fa-input" type="password" value={state.password} onChange={e => setState({ ...state, password: e.target.value })} className="bg-white/5 border-white/10 text-white h-11" placeholder="Mot de passe cloud" autoFocus onKeyDown={e => e.key === 'Enter' && verify2FA()} />
            <div className="flex justify-end gap-2"><Button variant="outline" className="border-white/10 bg-white/5 text-slate-300 hover:bg-white/10" onClick={onClose}>Annuler</Button>
              <Button data-testid="reconnect-2fa-submit" className="bg-emerald-500 hover:bg-emerald-600" onClick={verify2FA} disabled={saving || !state.password}>{saving ? 'Vérification…' : 'Connecter'}</Button>
            </div>
          </div>
        )}

        {state.step === 'success' && state.me && (
          <div className="pt-2 space-y-4">
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5 text-center space-y-2">
              <CheckCircle2 size={40} className="text-emerald-400 mx-auto" />
              <div className="text-base font-semibold text-white">Connecté !</div>
              <div className="text-sm text-emerald-200">{[state.me.first_name, state.me.last_name].filter(Boolean).join(' ')}{state.me.username && <span className="text-emerald-300/80"> · @{state.me.username}</span>}</div>
              <div className="text-[10px] text-emerald-200/60 pt-1">Visible sur Telegram comme : iPhone 15 Pro Max · iOS 17.4 · Français · Paris · Decodo #{state.proxyId}</div>
            </div>
            <Button data-testid="reconnect-done" className="w-full bg-blue-500 hover:bg-blue-600" onClick={onDone}>Terminé</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const DEFAULT_NOTES_TEMPLATE = '- Âge :\n- Job :\n- Hobbies :\n- Préférences :\n- GF :\n- Jour de paie :\n- Notes :';

function EditAccountDialog({ account, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);

  useEffect(() => {
    if (account) setForm({
      name: account.name || '',
      username: account.username || '',
      telegram_bio: account.telegram_bio || '',
      default_nickname: account.default_nickname || '',
      auto_birthday_enabled: account.auto_birthday_enabled || false,
      auto_birthday_message: account.auto_birthday_message || '',
      internal_bio: account.internal_bio || '',
      paid_dms: account.paid_dms || false,
      notes_template: account.notes_template || DEFAULT_NOTES_TEMPLATE,
    });
    // Pre-fill bio + username from the LIVE Telegram profile so the form always
    // matches what's actually configured on Telegram (covers cases where the
    // user updated bio/username from the official Telegram app).
    if (account && account.status === 'ready') {
      setLoadingProfile(true);
      api.get(`/telegram/profile/${account.id}`)
        .then(({ data }) => {
          setForm((f) => ({
            ...f,
            name: f.name || [data.first_name, data.last_name].filter(Boolean).join(' ') || account.name || '',
            username: f.username || (data.username || ''),
            telegram_bio: f.telegram_bio || (data.bio || ''),
          }));
        })
        .catch(() => {})
        .finally(() => setLoadingProfile(false));
    }
  }, [account]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name?.trim()) { toast.error(t('dash.edit.errName')); return; }
    if (form.username && !/^[a-zA-Z0-9_]+$/.test(form.username)) { toast.error(t('dash.edit.errUsername')); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/accounts/${account.id}`, form);
      toast.success(t('dash.edit.saved'));
      onSaved(data);
    } catch (e) { toast.error(apiError(e, t('dash.edit.saveFailed'))); } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!account} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200 max-w-lg max-h-[85vh] overflow-y-auto scrollbar-thin">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2"><span className="w-1 h-5 bg-indigo-500 rounded-full" /> {t('dash.edit.title')}{loadingProfile && <span className="text-[10px] text-slate-500 font-normal">{t('dash.edit.loadingProfile')}</span>}</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">{t('dash.edit.desc')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs flex items-center gap-1.5"><User size={12} /> {t('dash.edit.tgName')} <span className="text-rose-400">*</span></Label>
            <Input data-testid="edit-acc-name" value={form.name || ''} onChange={e => set('name', e.target.value)} className="bg-white/5 border-white/10 text-white h-10" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs flex items-center gap-1.5"><AtSign size={12} /> {t('dash.edit.username')}</Label>
            <div className="flex">
              <span className="px-3 flex items-center rounded-l-lg bg-white/10 border border-r-0 border-white/10 text-slate-400 text-sm">@</span>
              <Input data-testid="edit-acc-username" value={form.username || ''} onChange={e => set('username', e.target.value)} className="bg-white/5 border-white/10 text-white h-10 rounded-l-none" placeholder="emma_paris" />
            </div>
            <p className="text-[10px] text-slate-500">{t('dash.edit.usernameHelp')}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs flex items-center gap-1.5"><MessageSquare size={12} /> {t('dash.edit.tgBio')}</Label>
            <textarea data-testid="edit-acc-bio" value={form.telegram_bio || ''} onChange={e => set('telegram_bio', e.target.value.slice(0, 130))} rows={2}
              className="w-full rounded-lg border border-white/10 bg-white/5 p-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-indigo-500/40 resize-none" placeholder="Coucou 🌸 voyageuse et passionnée d'art" />
            <div className="flex justify-between text-[10px] text-slate-500"><span>{t('dash.edit.tgBioHelp')}</span><span>{(form.telegram_bio || '').length}/130</span></div>
          </div>
          <div className="space-y-1.5 pt-1 border-t border-white/5">
            <Label className="text-slate-300 text-xs flex items-center gap-1.5 pt-2"><User size={12} /> {t('dash.edit.nickname')}</Label>
            <Input data-testid="edit-acc-nickname" value={form.default_nickname || ''} onChange={e => set('default_nickname', e.target.value)} className="bg-white/5 border-white/10 text-white h-10" placeholder={t('dash.edit.nicknamePlaceholder')} />
            <p className="text-[10px] text-slate-500">{t('dash.edit.nicknameHelp')}</p>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-slate-300 text-xs flex items-center gap-1.5"><Gift size={12} /> {t('dash.edit.birthday')}</Label>
              <div className="flex items-center gap-2">
                <span className="text-[10px] px-2 py-0.5 rounded-lg border border-white/10 bg-white/5 text-slate-400">{'{name}'}</span>
                <Switch data-testid="edit-acc-birthday-toggle" checked={form.auto_birthday_enabled || false} onCheckedChange={v => set('auto_birthday_enabled', v)} className="data-[state=checked]:bg-indigo-500" />
              </div>
            </div>
            <textarea data-testid="edit-acc-birthday-msg" value={form.auto_birthday_message || ''} onChange={e => set('auto_birthday_message', e.target.value)} rows={2}
              className="w-full rounded-lg border border-white/10 bg-white/5 p-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-indigo-500/40 resize-none" placeholder={t('dash.edit.birthdayPlaceholder')} />
            <p className="text-[10px] text-slate-500">{t('dash.edit.birthdayHelp')}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs flex items-center gap-1.5"><MessageSquare size={12} /> {t('dash.edit.internalBio')}</Label>
            <textarea data-testid="edit-acc-internal-bio" value={form.internal_bio || ''} onChange={e => set('internal_bio', e.target.value)} rows={2}
              className="w-full rounded-lg border border-white/10 bg-white/5 p-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-indigo-500/40 resize-none" placeholder={t('dash.edit.internalBioPlaceholder')} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-slate-300 text-xs flex items-center gap-1.5"><Star size={12} className="text-amber-400" /> {t('dash.edit.paidDms')}</Label>
              <p className="text-[10px] text-slate-500 mt-0.5">{t('dash.edit.paidDmsHelp')}</p>
            </div>
            <Switch data-testid="edit-acc-paid-dms" checked={form.paid_dms || false} onCheckedChange={v => set('paid_dms', v)} className="data-[state=checked]:bg-indigo-500" />
          </div>
          <div className="space-y-1.5 pt-1 border-t border-white/5">
            <Label className="text-slate-300 text-xs flex items-center gap-1.5 pt-2"><FileText size={12} /> {t('dash.edit.notesTemplate')}</Label>
            <textarea data-testid="edit-acc-notes-template" value={form.notes_template || ''} onChange={e => set('notes_template', e.target.value)} rows={8}
              className="w-full rounded-lg border border-white/10 bg-white/5 p-2.5 text-xs text-white placeholder:text-slate-600 outline-none focus:border-indigo-500/40 resize-none font-mono leading-relaxed" />
            <p className="text-[10px] text-slate-500">{t('dash.edit.notesTemplateHelp')}</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" className="border-white/10 bg-white/5 text-slate-300 hover:bg-white/10" onClick={onClose}>{t('dash.edit.cancel')}</Button>
            <Button data-testid="edit-acc-save" className="bg-blue-500 hover:bg-blue-600" onClick={save} disabled={saving}>{saving ? t('dash.edit.saving') : t('dash.edit.save')}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FakeQR() {
  const cells = React.useMemo(() => Array.from({ length: 441 }, (_, i) => ((i * 2654435761) >>> 0) % 5 < 2), []);
  return (
    <div className="w-44 h-44 bg-white rounded-xl p-2.5 mx-auto">
      <div className="grid w-full h-full" style={{ gridTemplateColumns: 'repeat(21, 1fr)' }}>
        {cells.map((on, i) => <div key={i} className={on ? 'bg-slate-900' : 'bg-white'} />)}
      </div>
    </div>
  );
}

function NewAccount({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('choose'); // choose | qr | phone | code | twofa | success
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [accountId, setAccountId] = useState(null);
  const [proxyId, setProxyId] = useState(null);
  const [me, setMe] = useState(null);
  const [boxes, setBoxes] = useState([3]);
  const [saving, setSaving] = useState(false);
  const colors = ['from-indigo-500 to-violet-500', 'from-cyan-500 to-blue-500', 'from-pink-500 to-rose-500', 'from-amber-500 to-orange-500', 'from-emerald-500 to-teal-500'];

  useEffect(() => {
    if (open) { setStep('choose'); setName(''); setPhone(''); setCode(''); setPassword(''); setAccountId(null); setProxyId(null); setMe(null); setBoxes([3]); }
  }, [open]);

  // Step 1: Create the account + trigger Telegram SMS code
  const startConnect = async () => {
    if (!name.trim()) { toast.error('Nom du compte requis'); return; }
    if (!phone.trim()) { toast.error('Numéro de téléphone requis'); return; }
    setSaving(true);
    try {
      const { data: acc } = await api.post('/accounts', { name, phone, boxes: boxes[0], color: colors[Math.floor(Math.random() * colors.length)] });
      setAccountId(acc.id);
      const { data: res } = await api.post('/telegram/connect/start', { account_id: acc.id, phone });
      setProxyId(res.proxy_session_id);
      toast.success(`Code envoyé sur Telegram · Session proxy ${res.proxy_session_id}`);
      setStep('code');
    } catch (e) {
      toast.error(apiError(e, 'Échec du démarrage de la connexion'));
    } finally { setSaving(false); }
  };

  // Step 2: Submit SMS code
  const verifyCode = async () => {
    if (!code.trim()) { toast.error('Code requis'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/telegram/connect/verify', { account_id: accountId, code });
      if (data.needs_password) {
        toast.info('2FA activée — saisis ton mot de passe cloud');
        setStep('twofa');
      } else {
        setMe(data.me);
        toast.success(`Connecté en tant que ${data.me.first_name || data.me.username || data.me.phone}`);
        setStep('success');
        onCreated();
      }
    } catch (e) {
      toast.error(apiError(e, 'Code invalide'));
    } finally { setSaving(false); }
  };

  // Step 3: Submit 2FA password
  const verify2FA = async () => {
    if (!password) { toast.error('Mot de passe requis'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/telegram/connect/2fa', { account_id: accountId, password });
      setMe(data.me);
      toast.success(`Connecté en tant que ${data.me.first_name || data.me.username || data.me.phone}`);
      setStep('success');
      onCreated();
    } catch (e) {
      toast.error(apiError(e, 'Échec de la 2FA'));
    } finally { setSaving(false); }
  };


  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="add-account-button" className="bg-indigo-500 hover:bg-indigo-600"><Plus size={16} className="mr-1.5" /> Ajouter un compte</Button></DialogTrigger>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2"><span className="w-1 h-5 bg-blue-500 rounded-full" /> Ajouter un compte Telegram</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">Choisis comment connecter ton compte Telegram</DialogDescription>
        </DialogHeader>

        {step === 'choose' && (
          <div className="pt-2 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <button data-testid="connect-qr-option" onClick={() => setStep('qr')} className="rounded-2xl border border-white/10 bg-white/[0.02] hover:border-blue-500/40 hover:bg-blue-500/[0.04] p-6 flex flex-col items-center gap-3 transition-colors">
                <QrCode size={36} className="text-blue-400" />
                <div className="text-sm font-semibold text-white">QR code</div>
                <div className="text-[11px] text-slate-500">Scanner avec Telegram</div>
              </button>
              <button data-testid="connect-phone-option" onClick={() => setStep('phone')} className="rounded-2xl border border-white/10 bg-white/[0.02] hover:border-emerald-500/40 hover:bg-emerald-500/[0.04] p-6 flex flex-col items-center gap-3 transition-colors">
                <span className="w-14 h-14 rounded-full bg-white/5 flex items-center justify-center"><Smartphone size={24} className="text-slate-300" /></span>
                <div className="text-sm font-semibold text-white">Téléphone</div>
                <div className="text-[11px] text-slate-500">Code par SMS ou via l&apos;app</div>
                <span className="text-[10px] text-emerald-400 font-medium">Recommandé</span>
              </button>
            </div>
            <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.06] p-3 flex gap-2.5" data-testid="premium-recommendation">
              <Star size={15} className="text-amber-400 shrink-0 mt-0.5 fill-amber-400/30" />
              <div className="text-[11.5px] leading-relaxed text-amber-100/90">
                <span className="font-semibold text-amber-300">Astuce : un compte Telegram Premium est recommandé.</span> Il débloque plus de fonctionnalités (transcription des messages vocaux, envoi de fichiers jusqu&apos;à 4 Go, messages plus longs) et inspire plus confiance à Telegram — ce qui réduit le risque de restrictions, surtout pour les comptes à gros volume.
              </div>
            </div>
            <div className="flex justify-end pt-1 border-t border-white/5">
              <Button variant="outline" className="border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 mt-3" onClick={() => setOpen(false)}>Annuler</Button>
            </div>
          </div>
        )}

        {step === 'qr' && (
          <div className="pt-2 space-y-4">
            <FakeQR />
            <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.06] p-3 text-center">
              <p className="text-xs text-amber-200/90 font-medium">La connexion par QR n&apos;est pas encore disponible.</p>
              <p className="text-[11px] text-amber-100/70 mt-1">Utilise la méthode Téléphone — elle connecte le compte à Telegram immédiatement.</p>
            </div>
            <div className="flex justify-between pt-1">
              <Button variant="outline" className="border-white/10 bg-white/5 text-slate-300 hover:bg-white/10" onClick={() => setStep('choose')}>Retour</Button>
              <Button className="bg-emerald-500 hover:bg-emerald-600" onClick={() => setStep('phone')}>Utiliser la méthode Téléphone</Button>
            </div>
          </div>
        )}

        {step === 'phone' && (
          <div className="pt-2 space-y-4">
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Nom du compte</Label><Input data-testid="account-name-input" value={name} onChange={e => setName(e.target.value)} className="bg-white/5 border-white/10 text-white h-10" placeholder="Emma Laurent" autoFocus /></div>
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Numéro de téléphone (international, avec +)</Label><Input data-testid="account-phone-input" value={phone} onChange={e => setPhone(e.target.value)} className="bg-white/5 border-white/10 text-white h-10" placeholder="+33612345678" /></div>
            <p className="text-[10px] text-slate-500">Tu vas recevoir un code de connexion dans ton app Telegram (ou par SMS si elle n&apos;est pas installée). Connexion routée via le proxy mobile Decodo · Paris · spoof iPhone 15 Pro.</p>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-slate-300 text-xs">Répartition des boxes</Label>
                <span className="text-sm font-semibold text-indigo-400">{boxes[0]} boxes</span>
              </div>
              <Slider min={2} max={9} step={1} value={boxes} onValueChange={setBoxes} />
            </div>
            <div className="flex justify-between pt-3 border-t border-white/5">
              <Button variant="outline" className="border-white/10 bg-white/5 text-slate-300 hover:bg-white/10" onClick={() => setStep('choose')}>Retour</Button>
              <Button data-testid="account-save-button" className="bg-blue-500 hover:bg-blue-600" onClick={startConnect} disabled={saving}>{saving ? 'Envoi du code…' : 'Envoyer le code'}</Button>
            </div>
          </div>
        )}

        {step === 'code' && (
          <div className="pt-2 space-y-4">
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-300">
              ✓ Code envoyé sur Telegram <b>{phone}</b>. Vérifie ton app Telegram (ou tes SMS).
              <div className="text-[10px] text-emerald-200/70 mt-1">Decodo Paris · session #{proxyId}</div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs">Code Telegram (5 chiffres)</Label>
              <Input data-testid="account-code-input" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 5))} className="bg-white/5 border-white/10 text-white h-12 text-center text-2xl tracking-widest font-mono" placeholder="12345" autoFocus
                onKeyDown={e => e.key === 'Enter' && verifyCode()} />
            </div>
            <p className="text-[10px] text-slate-500">Si tu ne le reçois pas, va dans Telegram &gt; Paramètres &gt; Connexions récentes, ou attends ~60s pour le repli SMS.</p>
            <div className="flex justify-between pt-3 border-t border-white/5">
              <Button variant="outline" className="border-white/10 bg-white/5 text-slate-300 hover:bg-white/10" onClick={async () => { if (accountId) await api.post('/telegram/connect/cancel', { account_id: accountId }).catch(() => {}); setStep('phone'); }}>Annuler</Button>
              <Button data-testid="verify-code-button" className="bg-blue-500 hover:bg-blue-600" onClick={verifyCode} disabled={saving || code.length < 4}>{saving ? 'Vérification…' : 'Vérifier le code'}</Button>
            </div>
          </div>
        )}

        {step === 'twofa' && (
          <div className="pt-2 space-y-4">
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-300">
              🔐 Mot de passe cloud 2FA activé sur ce compte. Saisis-le pour terminer.
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs">Mot de passe cloud 2FA</Label>
              <Input data-testid="account-2fa-input" type="password" value={password} onChange={e => setPassword(e.target.value)} className="bg-white/5 border-white/10 text-white h-11" placeholder="Ton mot de passe cloud Telegram" autoFocus
                onKeyDown={e => e.key === 'Enter' && verify2FA()} />
            </div>
            <div className="flex justify-between pt-3 border-t border-white/5">
              <Button variant="outline" className="border-white/10 bg-white/5 text-slate-300 hover:bg-white/10" onClick={() => setStep('code')}>Retour</Button>
              <Button data-testid="verify-2fa-button" className="bg-blue-500 hover:bg-blue-600" onClick={verify2FA} disabled={saving || !password}>{saving ? 'Vérification…' : 'Connecter'}</Button>
            </div>
          </div>
        )}

        {step === 'success' && me && (
          <div className="pt-2 space-y-4">
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5 text-center space-y-2">
              <CheckCircle2 size={40} className="text-emerald-400 mx-auto" />
              <div className="text-base font-semibold text-white">Connecté !</div>
              <div className="text-sm text-emerald-200">
                {[me.first_name, me.last_name].filter(Boolean).join(' ')}
                {me.username && <span className="text-emerald-300/80"> · @{me.username}</span>}
              </div>
              <div className="text-[11px] text-emerald-200/70">ID Telegram {me.id} · {me.phone}</div>
              <div className="text-[10px] text-emerald-200/60 pt-1">Apparaît sur Telegram comme : iPhone 15 Pro Max · iOS 17.4 · Français · Paris · Decodo #{proxyId}</div>
            </div>
            <Button data-testid="account-done-button" className="w-full bg-blue-500 hover:bg-blue-600" onClick={() => setOpen(false)}>Terminé</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
